/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { ApplicationEntityType } from "@advapacs/common";

export const DEFAULT_OFFSET = 0;
export const DEFAULT_LIMIT = 25;

export const Viewer = {
  EUNITY: "EUNITY",
  MEDDREAM: "MEDDREAM",
  ZED: "ZED",
  ADVAVIEW: "ADVAVIEW"
};

export enum MoveTaskStatus {
  QUEUED = "QUEUED",
  IN_PROGRESS = "IN_PROGRESS",
  PENDING_RETRY = "PENDING_RETRY",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
  COMPLETED = "COMPLETED",
}

export enum TransformRuleTarget {
  GATEWAY = "GATEWAY",
  CLOUD = "CLOUD",
}

export enum CompletionType {
  ALL_ASSIGNEES = "ALL",
  ANY_ASSIGNEE = "ANY",
}

export const MoveTaskStatusOptions = [
  {
    label: "Queued",
    value: MoveTaskStatus.QUEUED,
  },
  {
    label: "In Progress",
    value: MoveTaskStatus.IN_PROGRESS,
  },
  {
    label: "Pending Retry",
    value: MoveTaskStatus.PENDING_RETRY,
  },
  {
    label: "Failed",
    value: MoveTaskStatus.FAILED,
  },
  {
    label: "Cancelled",
    value: MoveTaskStatus.CANCELLED,
  },
  {
    label: "Completed",
    value: MoveTaskStatus.COMPLETED,
  },
];

export const transformRuleTargetOptions = [
  {
    label: "AdvaPACS Gateway",
    value: TransformRuleTarget.GATEWAY,
  },
  {
    label: "AdvaPACS Cloud",
    value: TransformRuleTarget.CLOUD,
  },
];

export const RETENTION_TIME_OPTION = [
  {
    label: "DAYS",
    value: "DAYS",
  },
  {
    label: "MONTHS",
    value: "MONTHS",
  },
];

export const DELAY_AFTER_OPTIONS = [
  {
    label: "HOUR",
    value: "HOUR",
  },
  {
    label: "DAYS",
    value: "DAYS",
  },
];

export const ruleLevelOptions = [
  {
    label: "STUDY",
    value: "STUDY",
  },
  {
    label: "SERIES",
    value: "SERIES",
  },
  {
    label: "INSTANCE",
    value: "INSTANCE",
  },
];

export const triggerOptions = [
  {
    label: "SEND",
    value: "SEND",
  },
  {
    label: "RECEIVE",
    value: "RECEIVE",
  },
];

export enum UsernameTransform {
  NONE = "NONE",
  FROM_EMAIL = "FROM_EMAIL",
}

export enum StudyCase {
  CURRENT_STUDY_NEW_SERIES = "CURRENT_STUDY_NEW_SERIES",
  OTHER_STUDY_NEW_SERIES = "OTHER_STUDY_NEW_SERIES",
  OTHER_STUDY_EXISTING_SERIES = "OTHER_STUDY_EXISTING_SERIES",
  CURRENT_STUDY_EXISTING_SERIES = "CURRENT_STUDY_EXISTING_SERIES",
  NEW_STUDY = "NEW_STUDY",
  NEW_STUDY_EXISTING_ORDER = "NEW_STUDY_EXISTING_ORDER",
}

export enum EditSeriesModalType {
  MOVE_IMAGE_TO_NEW_SERIES = "MOVE_IMAGE_TO_NEW_SERIES",
  COPY_IMAGE_TO_NEW_SERIES = "COPY_IMAGE_TO_NEW_SERIES",
  MOVE_IMAGE_TO_EXISTING_SERIES = "MOVE_IMAGE_TO_EXISTING_SERIES",
  COPY_IMAGE_TO_EXISTING_SERIES = "COPY_IMAGE_TO_EXISTING_SERIES",
  DELETE_IMAGE_IN_SERIES = "DELETE_IMAGE_IN_SERIES",
}

export enum DocumentStatus {
  preliminary = "Preliminary",
  final = "Final",
  amended = "Amended",
  entered_in_error = "Entered In Error",
  registered = "Registered",
  partial = "Partial",
  corrected = "Corrected",
  appended = "Appended",
  cancelled = "Cancelled",
  deprecated = "Deprecated",
  unknown = "Unknown",
}

export enum DocumentTableStatus {
  current = "Current",
  superseded = "Superseded",
  entered_in_error = "Entered In Error",
}

export enum TriggerPrefetching {
  RECEIVE_NEW_STUDY = "RECEIVE_NEW_STUDY",
  ORDER_CREATED = "ORDER_CREATED",
}

export const PrefetchingRuleTriggerOptionsWithOrder = [
  {
    value: TriggerPrefetching.RECEIVE_NEW_STUDY,
    label: "Receive New Study",
  },
  {
    value: TriggerPrefetching.ORDER_CREATED,
    label: "Order Created",
  },
];

export enum DuplicateMode {
  IGNORE = "IGNORE",
  OVERWRITE = "OVERWRITE",
}

export const applicationEntityTypeOptions = [
  {
    label: "AdvaPACS Gateway",
    value: ApplicationEntityType.GATEWAY,
  },
  {
    label: "AdvaPACS Cloud",
    value: ApplicationEntityType.CLOUD,
  },
];

export enum QueryGranularityOption {
  ONE_HOUR = "1H",
  TWO_HOUR = "2H",
  THREE_HOUR = "3H",
  FOUR_HOUR = "4H",
  FIVE_HOUR = "5H",
  SIX_HOUR = "6H",
  SEVEN_HOUR = "7H",
  EIGHT_HOUR = "8H",
  NINE_HOUR = "9H",
  TEN_HOUR = "10H",
  ELEVEN_HOUR = "11H",
  TWELVE_HOUR = "12H",
}

export const TIME_OPTIONS = [
  {
    label: QueryGranularityOption.ONE_HOUR,
    value: QueryGranularityOption.ONE_HOUR,
  },
  {
    label: QueryGranularityOption.TWO_HOUR,
    value: QueryGranularityOption.TWO_HOUR,
  },
  {
    label: QueryGranularityOption.THREE_HOUR,
    value: QueryGranularityOption.THREE_HOUR,
  },
  {
    label: QueryGranularityOption.FOUR_HOUR,
    value: QueryGranularityOption.FOUR_HOUR,
  },
  {
    label: QueryGranularityOption.FIVE_HOUR,
    value: QueryGranularityOption.FIVE_HOUR,
  },
  {
    label: QueryGranularityOption.SIX_HOUR,
    value: QueryGranularityOption.SIX_HOUR,
  },
  {
    label: QueryGranularityOption.SEVEN_HOUR,
    value: QueryGranularityOption.SEVEN_HOUR,
  },
  {
    label: QueryGranularityOption.EIGHT_HOUR,
    value: QueryGranularityOption.EIGHT_HOUR,
  },
  {
    label: QueryGranularityOption.NINE_HOUR,
    value: QueryGranularityOption.NINE_HOUR,
  },
  {
    label: QueryGranularityOption.TEN_HOUR,
    value: QueryGranularityOption.TEN_HOUR,
  },
  {
    label: QueryGranularityOption.ELEVEN_HOUR,
    value: QueryGranularityOption.ELEVEN_HOUR,
  },
  {
    label: QueryGranularityOption.TWELVE_HOUR,
    value: QueryGranularityOption.TWELVE_HOUR,
  },
];

export enum MigrationJobStatus {
  INDEXING = "Indexing",
  PENDING_MIGRATION = "Pending Migration",
  MIGRATING = "Migrating",
  COMPLETED = "Completed",
  CANCELLED = "Cancelled",
  ERROR = "Error",
  VERIFYING = "Verifying",
  RECURRING = "Recurring",
}

export enum MigrationIndexedStudyStatus {
  MIGRATED = "Migrated",
  INDEXED = "Indexed",
  PENDING_VERIFICATION = "Pending Verification",
  PARTIALLY_MIGRATED = "Partially Migrated",
  FAILED = "Failed",
  RETRIEVING = "Retrieving",
  NEEDS_VALIDATION = "Needs Validation",
}

export enum HL7ServiceType {
  GATEWAY = "GATEWAY",
  CLOUD = "CLOUD",
  HTTPS = "HTTPS",
}

export enum HL7ServiceMode {
  TCP = "TCP",
  FILE = "FILE",
}

export enum ImageLifecycleManagementTaskStatus {
  PENDING = "Pending",
  PROCESSING = "Processing",
  WAITING_FOR_RESTORE = "Waiting For Restore",
  COMPLETED = "Completed",
  CANCELLED = "Cancelled",
  FAILED = "Failed",
}

export enum ViewDiagnosticReportMode {
  STUDY = "STUDY",
  ORDER = "ORDER",
}

export const HL7ServiceTypeOptions = [
  {
    label: "AdvaPACS Gateway",
    value: HL7ServiceType.GATEWAY,
  },
  {
    label: "AdvaPACS Cloud",
    value: HL7ServiceType.CLOUD,
  },
  {
    label: "HL7 Over HTTPS",
    value: HL7ServiceType.HTTPS,
  },
];

export const HL7ServiceModeOptions = [
  {
    label: "TCP",
    value: HL7ServiceMode.TCP,
  },
  {
    label: "File",
    value: HL7ServiceMode.FILE,
  },
];

export enum InterfaceVersion {
  TWENTY_TWENTY_TWO_SEPTEMBER_SIXTEENTH = "2022-09-16",
  TWENTY_TWENTY_THREE_NOVEMBER_SIXTEENTH = "2023-11-16",
  TWENTY_TWENTY_FOUR_FEBRUARY_ONE = "2024-02-01",
  TWENTY_TWENTY_FOUR_OCTOBER_FOUR = "2024-10-04",
  TWENTY_TWENTY_FIVE_SEPTEMBERS_TEN = "2025-09-10",
}

export const MONTH_STR = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
];

export enum TaskStatus {
  TODO = "TODO",
  COMPLETED = "COMPLETED",
}
